import { signOut, User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';

const ACCESS_DENIED_ERROR = 'Access denied: Admins only';
const ADMIN_CACHE_TTL_MS = 30_000;

let cachedAdminEmail: string | null = null;
let cachedAdminEnabled = false;
let cachedAdminCheckedAt = 0;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForAuthenticatedUser = async (
  timeoutMs = 10_000
): Promise<User | null> => {
  const existingUser = auth.currentUser;
  if (existingUser) {
    return existingUser;
  }

  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const user = auth.currentUser;
    if (user) {
      return user;
    }

    await sleep(100);
  }

  return auth.currentUser;
};

const getIsEnabledAdmin = async (email: string, forceRefresh: boolean) => {
  const now = Date.now();
  const cacheValid =
    !forceRefresh &&
    cachedAdminEmail === email &&
    now - cachedAdminCheckedAt < ADMIN_CACHE_TTL_MS;

  if (cacheValid) {
    return cachedAdminEnabled;
  }

  const adminSnap = await getDoc(doc(db, 'admins', email));
  const isEnabledAdmin = adminSnap.exists() && !!adminSnap.data().enabled;

  cachedAdminEmail = email;
  cachedAdminEnabled = isEnabledAdmin;
  cachedAdminCheckedAt = now;

  return isEnabledAdmin;
};

export const isPermissionDeniedError = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const code =
    'code' in error && typeof error.code === 'string' ? error.code : '';
  const message =
    'message' in error && typeof error.message === 'string'
      ? error.message
      : '';
  const status =
    'status' in error &&
    (typeof error.status === 'number' || typeof error.status === 'string')
      ? String(error.status)
      : '';

  return (
    code.includes('permission-denied') ||
    message.includes('permission-denied') ||
    message.includes('Missing or insufficient permissions') ||
    status === '403'
  );
};

export const ensureAdminSession = async ({
  forceRefresh = false,
  signOutIfInvalid = true,
  currentUser,
}: {
  forceRefresh?: boolean;
  signOutIfInvalid?: boolean;
  currentUser?: User | null;
} = {}) => {
  const user = currentUser ?? auth.currentUser ?? (await waitForAuthenticatedUser());

  if (!user) {
    throw new Error('Authentication required');
  }

  if (forceRefresh) {
    await user.getIdToken(true);
    // Allow a short delay for refreshed custom claims to propagate.
    await sleep(250);
  }

  const tokenResult = await user.getIdTokenResult();
  const email = user.email;

  if (!email) {
    if (signOutIfInvalid) {
      await signOut(auth);
    }
    throw new Error(ACCESS_DENIED_ERROR);
  }

  const isEnabledAdmin = await getIsEnabledAdmin(email, forceRefresh);

  const hasAdminClaim = !!tokenResult.claims.admin;

  if (!hasAdminClaim && !isEnabledAdmin) {
    if (signOutIfInvalid) {
      await signOut(auth);
    }
    throw new Error(ACCESS_DENIED_ERROR);
  }

  return { user, claims: tokenResult.claims };
};

export const withAdminSessionRetry = async <T>(
  operation: () => Promise<T>
): Promise<T> => {
  await ensureAdminSession();

  try {
    return await operation();
  } catch (error) {
    if (!isPermissionDeniedError(error)) {
      throw error;
    }

    await ensureAdminSession({ forceRefresh: true, signOutIfInvalid: false });
    return operation();
  }
};


export const ensureAdminSessionEventually = async ({
  timeoutMs = 10_000,
  retryEveryMs = 250,
}: {
  timeoutMs?: number;
  retryEveryMs?: number;
} = {}) => {
  const user = await waitForAuthenticatedUser(timeoutMs);

  if (!user) {
    throw new Error('Authentication required');
  }

  const startedAt = Date.now();
  let lastError: unknown = null;

  while (Date.now() - startedAt < timeoutMs) {
    try {
      return await ensureAdminSession({
        forceRefresh: true,
        signOutIfInvalid: false,
        currentUser: user,
      });
    } catch (error) {
      lastError = error;
      const message = error instanceof Error ? error.message : '';
      if (message !== ACCESS_DENIED_ERROR && message !== 'Authentication required') {
        throw error;
      }
      await sleep(retryEveryMs);
    }
  }

  if (lastError) {
    throw lastError;
  }

  throw new Error(ACCESS_DENIED_ERROR);
};
